import React, { useCallback, useEffect, useState } from "react";
import { MediaUrl } from "../shared/types";

// ----------------------------  MediaGallery  ----------------------------
// Shows every image from a sample's S3 folder together in one scrollable panel,
// two per row and sized so two rows (four images) fit in the window.
// Clicking an image opens it larger in a popup with previous / next controls.
// Non-image media render as links below the images.

interface MediaGalleryProps {
  media: MediaUrl[];
}

const MediaGallery: React.FC<MediaGalleryProps> = ({ media }) => {
  const images = media.filter((m) => m.type === "image" && !!m.url);
  const others = media.filter((m) => !(m.type === "image" && !!m.url));
  const [open, setOpen] = useState<number | null>(null);
  const [failed, setFailed] = useState<Record<string, boolean>>({});

  // A new task brings a new image set.
  useEffect(() => {
    setOpen(null);
    setFailed({});
  }, [media]);

  const count = images.length;
  const markFailed = (url: string) => setFailed((f) => ({ ...f, [url]: true }));
  const close = useCallback(() => setOpen(null), []);
  const go = useCallback(
    (delta: number) => setOpen((i) => (i === null ? i : (i + delta + count) % count)),
    [count],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {count > 0 && (
        <div
          style={{ ...scrollPanel, gridTemplateColumns: count > 1 ? "1fr 1fr" : "1fr" }}
          data-testid="media-scroll-panel"
        >
          {images.map((m, i) => (
            <figure key={m.url} style={figure}>
              {failed[m.url] ? (
                <div style={brokenBox}>
                  Image failed to load.{" "}
                  <a href={m.url} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                  </a>
                </div>
              ) : (
                <button
                  type="button"
                  style={imageBtn}
                  onClick={() => setOpen(i)}
                  aria-label={"Open " + (m.label || "image " + (i + 1))}
                  title="Click to enlarge"
                >
                  <img
                    src={m.url}
                    alt={m.label || "image " + (i + 1)}
                    loading="lazy"
                    style={panelImage}
                    onError={() => markFailed(m.url)}
                  />
                </button>
              )}
              <figcaption style={caption} title={m.label}>
                {m.label || "Image"}
                {count > 1 && (
                  <span style={{ color: "#9ca3af", marginLeft: "8px" }}>
                    {i + 1} / {count}
                  </span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {others.map((m, i) => (
        <div key={i} style={brokenBox}>
          <span style={{ color: "#9ca3af", marginRight: "8px" }}>{m.type}</span>
          {m.url ? (
            <a href={m.url} target="_blank" rel="noopener noreferrer">
              {m.label || "Open"}
            </a>
          ) : (
            m.label
          )}
        </div>
      ))}

      {open !== null && images[open] && (
        <ImageLightbox
          image={images[open]}
          index={open}
          count={count}
          onClose={close}
          onPrev={() => go(-1)}
          onNext={() => go(1)}
        />
      )}
    </div>
  );
};

// ----------------------------  ImageLightbox  ----------------------------
// Popup with one image at a large size. Escape closes; arrow keys move.

interface ImageLightboxProps {
  image: MediaUrl;
  index: number;
  count: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  image,
  index,
  count,
  onClose,
  onPrev,
  onNext,
}) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && count > 1) onPrev();
      else if (e.key === "ArrowRight" && count > 1) onNext();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, onPrev, onNext, count]);

  return (
    <div
      style={overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <div style={lightbox} onClick={(e) => e.stopPropagation()}>
        <div style={lightboxHeader}>
          <div style={{ ...caption, textAlign: "left" }} title={image.label}>
            {image.label || "Image"}
            {count > 1 && (
              <span style={{ color: "#9ca3af", marginLeft: "8px" }}>
                {index + 1} / {count}
              </span>
            )}
          </div>
          <a href={image.url} target="_blank" rel="noopener noreferrer" style={headerLink}>
            Open in new tab
          </a>
          <button type="button" style={navBtn} onClick={onClose} aria-label="Close image">
            ✕
          </button>
        </div>
        <div style={lightboxBody}>
          {count > 1 && (
            <button type="button" style={navBtn} onClick={onPrev} aria-label="Previous image">
              &lsaquo;
            </button>
          )}
          <img src={image.url} alt={image.label || "image " + (index + 1)} style={largeImage} />
          {count > 1 && (
            <button type="button" style={navBtn} onClick={onNext} aria-label="Next image">
              &rsaquo;
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const scrollPanel: React.CSSProperties = {
  display: "grid",
  gap: "8px",
  alignItems: "start",
  maxHeight: "75vh",
  overflowY: "auto",
  paddingRight: "4px",
};

const figure: React.CSSProperties = {
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const imageBtn: React.CSSProperties = {
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "zoom-in",
  display: "block",
  width: "100%",
};

// Two rows plus captions fit the 75vh panel.
const panelImage: React.CSSProperties = {
  width: "100%",
  height: "calc((75vh - 60px) / 2)",
  objectFit: "contain",
  borderRadius: "8px",
  background: "#f3f4f6",
  display: "block",
};

const caption: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: "center",
  fontSize: "1rem",
  color: "#374151",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const navBtn: React.CSSProperties = {
  border: "1px solid #d1d5db",
  background: "white",
  borderRadius: "6px",
  padding: "6px 12px",
  fontSize: "1.4rem",
  lineHeight: 1,
  color: "#374151",
  cursor: "pointer",
  flexShrink: 0,
};

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.75)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: "24px",
};

const lightbox: React.CSSProperties = {
  background: "white",
  borderRadius: "10px",
  width: "100%",
  maxWidth: "95vw",
  maxHeight: "95vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const lightboxHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "8px 12px",
  borderBottom: "1px solid #e5e7eb",
};

const headerLink: React.CSSProperties = {
  fontSize: "1rem",
  flexShrink: 0,
};

const lightboxBody: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px",
  minHeight: 0,
  flex: 1,
};

const largeImage: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: "100%",
  maxHeight: "calc(95vh - 90px)",
  objectFit: "contain",
  display: "block",
};

const brokenBox: React.CSSProperties = {
  background: "#f3f4f6",
  borderRadius: "8px",
  padding: "16px",
  textAlign: "center",
  fontSize: "1rem",
  color: "#6b7280",
};

export default MediaGallery;
